import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Plus, Search, Edit, Trash2, Upload, Download, FileSpreadsheet, Eye, Printer, X, ChevronRight, Package, MapPin, Phone, Clock, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input, Select, Label, Textarea } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { toast } from "../components/ui/Toast";
import { useI18n } from "../contexts/I18nContext";
import { formatVND, formatDate, formatDateTime, parseApiError} from "../lib/utils";
import { useSort } from "../hooks/useSort";
import { useFeature } from "../contexts/CreatorConfigContext";

const EMPTY = {
  name: "", code: "", nickname: "",
  phone: "", email: "", address: "", district: "", city: "",
  tax_id: "", group: "new", type: "retail", classification: "",
  assigned_user_id: "", max_debt_days: 0, max_debt_amount: 0, notes: "",
  opening_balance: 0,
};

export default function Customers() {
  const { t } = useI18n();
  const canCustomPrices = useFeature("customers.custom_prices");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const [district, setDistrict] = useState("");
  const [sort, setSort] = useState("created_at");
  const [direction, setDirection] = useState("desc");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importResult, setImportResult] = useState(null);
  const [dupCodes, setDupCodes] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const fileRef = useRef(null);

  // --- CÁC STATE CHO TÍNH NĂNG CHI TIẾT KHÁCH HÀNG ---
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeTab, setActiveTab] = useState("info"); // info | history | debt | prices
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [debtLedger, setDebtLedger] = useState([]);
  const [filterType, setFilterType] = useState("all");
  // Tab bảng giá riêng
  const [customerPrices, setCustomerPrices] = useState([]); // [{product_id, price}]
  const [allProducts, setAllProducts] = useState([]);
  const [savingPrices, setSavingPrices] = useState(false);

  // Chi tiết 1 đơn hàng cụ thể (xem bên trong modal KH)
  const [orderDetail, setOrderDetail] = useState(null);   // object đơn hàng đã load
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);

  const { sorted: sortedItems, handleSort, SortIcon } = useSort(items, "created_at", "desc");

  const load = async () => {
    const params = { sort, direction };
    if (q) params.q = q;
    if (group) params.group = group;
    if (district) params.district = district;
    const { data } = await api.get("/customers", { params });
    setItems(data);
  };

  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [group, district, sort, direction]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    // Lọc sạch các giá trị rác ("nan", "null", "undefined") trước khi đưa vào form
    const cleaned = Object.fromEntries(
      Object.entries(c).map(([k, v]) => [
        k,
        (typeof v === "string" && ["nan", "null", "undefined", "none"].includes(v.toLowerCase().trim()))
          ? ""
          : v
      ])
    );
    setForm({ ...EMPTY, ...cleaned });
    setOpen(true);
  };

  // --- HÀM XỬ LÝ LỌC NHANH THEO THỜI GIAN ---
  const applyQuickFilter = (type) => {
    setFilterType(type);

    if (type === "all") {
      setStartDate("");
      setEndDate("");
      return;
    }

    // Tính ngày theo giờ VN (UTC+7) để tránh lệch ngày
    const now = new Date();
    const vnNow = new Date(now.getTime() + 7 * 60 * 60 * 1000);
    const y = vnNow.getUTCFullYear();
    const m = vnNow.getUTCMonth();
    const d = vnNow.getUTCDate();
    const dow = vnNow.getUTCDay(); // 0=CN, 1=T2...

    const fmt = (dt) => {
      const yy = dt.getUTCFullYear();
      const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(dt.getUTCDate()).padStart(2, "0");
      return `${yy}-${mm}-${dd}`;
    };

    let start, end;

    if (type === "today") {
      start = new Date(Date.UTC(y, m, d));
      end = new Date(Date.UTC(y, m, d));
    } else if (type === "week") {
      const diffToMonday = dow === 0 ? -6 : 1 - dow;
      start = new Date(Date.UTC(y, m, d + diffToMonday));
      end = new Date(Date.UTC(y, m, d + diffToMonday + 6));
    } else if (type === "month") {
      start = new Date(Date.UTC(y, m, 1));
      end = new Date(Date.UTC(y, m + 1, 0));
    } else if (type === "year") {
      start = new Date(Date.UTC(y, 0, 1));
      end = new Date(Date.UTC(y, 11, 31));
    }

    setStartDate(fmt(start));
    setEndDate(fmt(end));
  };

  // --- XEM CHI TIẾT 1 ĐƠN HÀNG CỤ THỂ BÊN TRONG MODAL ---
  const openOrderDetail = async (orderId) => {
    if (!orderId) return;
    setOrderDetailLoading(true);
    setOrderDetail(null);
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrderDetail(data);
    } catch {
      toast({ title: "Không thể tải chi tiết đơn hàng", variant: "error" });
    } finally {
      setOrderDetailLoading(false);
    }
  };

  // --- HÀM XỬ LÝ KHI BẤM NÚT CHI TIẾT KH ---
  const openDetail = async (c) => {
    setSelectedCustomer(c);
    setActiveTab("info");
    setFilterType("all"); // Reset bộ lọc về tất cả
    setStartDate("");
    setEndDate("");
    setDetailOpen(true);
    
    // 1. Gọi API lấy lịch sử mua hàng
    try {
      const historyRes = await api.get("/orders", { params: { customer_id: c.customer_id } });
      setPurchaseHistory(historyRes.data || []);
    } catch (e) {
      console.error("Lỗi lấy lịch sử:", e);
      setPurchaseHistory([]);
    }
    
    // 2. Gọi API lấy sổ công nợ
    try {
      const debtRes = await api.get(`/debts/customers/${c.customer_id}/orders`);
      setDebtLedger(debtRes.data || []);
    } catch (e) {
      console.error("Lỗi lấy công nợ:", e);
      setDebtLedger([]);
    }

    // 3. Load giá riêng + danh sách sản phẩm
    try {
      const [pricesRes, prodsRes] = await Promise.all([
        api.get(`/customers/${c.customer_id}/prices`),
        api.get("/products"),
      ]);
      setCustomerPrices(pricesRes.data || []);
      setAllProducts(prodsRes.data || []);
    } catch (_) {
      setCustomerPrices([]);
    }
  };

  // --- BỘ LỌC NGÀY DÀNH CHO LỊCH SỬ & CÔNG NỢ ---
  const getFilteredData = (dataList) => {
    return dataList.filter((item) => {
      const rawDate = item.date || item.created_at || "";
      if (!rawDate) return true;
      // Convert sang giờ VN (UTC+7) trước khi lấy YYYY-MM-DD
      const dt = new Date(rawDate);
      const vnDt = new Date(dt.getTime() + 7 * 60 * 60 * 1000);
      const itemDateStr = vnDt.toISOString().slice(0, 10);
      if (startDate && itemDateStr < startDate) return false;
      if (endDate && itemDateStr > endDate) return false;
      return true;
    });
  };

  const filteredHistory = getFilteredData(purchaseHistory);
  const filteredDebts = getFilteredData(debtLedger);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/customers/${editing.customer_id}`, form);
      else await api.post("/customers", form);
      toast({ title: t("common.saved"), variant: "success" });
      setOpen(false);
      load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`${t("common.delete")} "${c.name}"?`)) return;
    try {
      await api.delete(`/customers/${c.customer_id}`);
      toast({ title: t("common.deleted"), variant: "success" });
      load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/customers/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "customers-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: "Tải mẫu thất bại", variant: "error" });
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customers-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: "Xuất Excel thất bại", variant: "error" });
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast({ title: `Đã nhập ${data.inserted} khách hàng`, description: `Bỏ qua: ${data.skipped}`, variant: "success" });
      load();
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi nhập file"), variant: "error" });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const checkDuplicateCodes = async () => {
    setDupLoading(true);
    try {
      const { data } = await api.get("/customers/duplicate-codes");
      setDupCodes(data);
      setDupOpen(true);
      if (data.length === 0) {
        toast({ title: "Không có mã KH bị trùng 🎉", variant: "success" });
      }
    } catch (err) {
      toast({ title: parseApiError(err, "Kiểm tra mã trùng thất bại"), variant: "error" });
    } finally {
      setDupLoading(false);
    }
  };

  const districts = Array.from(new Set(items.map((i) => i.district).filter(Boolean)));


  const handlePrintHistory = () => {
    const rows = filteredHistory.map((h, i) => `<tr>
      <td>${i+1}</td>
      <td>${h.order_code||'—'}</td>
      <td>${h.created_at?h.created_at.slice(0,10):'—'}</td>
      <td style="text-align:right;font-family:monospace">${(h.total||0).toLocaleString('vi-VN')}₫</td>
      <td>${h.status||'—'}</td>
    </tr>`).join('');
    const total = filteredHistory.reduce((s,h)=>s+(h.total||0),0);
    const win = window.open('','_blank');
    win.document.write(`<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
    <title>Lịch sử mua hàng — ${selectedCustomer?.name||''}</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px;text-align:left}
    th{background:#f0f0f0;font-size:11px;text-transform:uppercase}tfoot td{font-weight:bold;background:#e8f5e9}
    @media print{body{margin:10mm}}</style></head><body>
    <h2>Lịch sử mua hàng</h2>
    <p class="sub">Khách: <b>${selectedCustomer?.name||''}</b> · ${selectedCustomer?.phone||''} · ${filteredHistory.length} đơn</p>
    <table><thead><tr><th>STT</th><th>Mã đơn</th><th>Ngày mua</th><th style="text-align:right">Tổng tiền</th><th>Trạng thái</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="3" style="text-align:right">TỔNG CỘNG</td><td style="text-align:right;font-family:monospace">${total.toLocaleString('vi-VN')}₫</td><td></td></tr></tfoot>
    </table><script>window.onload=()=>{window.print()}<\/script></body></html>`);
    win.document.close();
  };

  const handlePrintDebt = () => {
    const rows = filteredDebts.map((d, i) => `<tr>
      <td>${i+1}</td>
      <td>${d.order_code||'—'}</td>
      <td>${d.created_at?d.created_at.slice(0,10):'—'}</td>
      <td style="text-align:right;font-family:monospace;color:#dc2626">${(d.remaining_amount||0).toLocaleString('vi-VN')}₫</td>
      <td style="text-align:right;font-family:monospace;color:#16a34a">${(d.paid_amount||0).toLocaleString('vi-VN')}₫</td>
      <td>${d.note||'—'}</td>
    </tr>`).join('');
    const totalDebt = filteredDebts.reduce((s,d)=>s+(d.remaining_amount||0),0);
    const win = window.open('','_blank');
    win.document.write(`<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
    <title>Sổ công nợ — ${selectedCustomer?.name||''}</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px;text-align:left}
    th{background:#f0f0f0;font-size:11px;text-transform:uppercase}tfoot td{font-weight:bold;background:#fee2e2}
    @media print{body{margin:10mm}}</style></head><body>
    <h2>Sổ công nợ</h2>
    <p class="sub">Khách: <b>${selectedCustomer?.name||''}</b> · ${selectedCustomer?.phone||''} · ${filteredDebts.length} phiếu</p>
    <table><thead><tr><th>STT</th><th>Mã phiếu</th><th>Ngày</th><th style="text-align:right">Còn nợ</th><th style="text-align:right">Đã trả</th><th>Ghi chú</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="3" style="text-align:right">TỔNG NỢ CÒN LẠI</td><td style="text-align:right;font-family:monospace;color:#dc2626">${totalDebt.toLocaleString('vi-VN')}₫</td><td colspan="2"></td></tr></tfoot>
    </table><script>window.onload=()=>{window.print()}<\/script></body></html>`);
    win.document.close();
  };
  return (
    <div className="space-y-6 animate-fade-in" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("customers.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1">{items.length} {t("common.total").toLowerCase()}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="customers-template">
            <FileSpreadsheet size={14} /> {t("customers.downloadTemplate")}
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" data-testid="customers-import-file" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="customers-import-button">
            <Upload size={14} /> {t("customers.importExcel")}
          </Button>
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="customers-export-button">
            <Download size={14} /> {t("customers.exportExcel")}
          </Button>
          <Button variant="outline" size="sm" onClick={checkDuplicateCodes} disabled={dupLoading} data-testid="customers-check-dup-codes">
            <Search size={14} /> {dupLoading ? "Đang kiểm tra..." : "Kiểm tra mã trùng"}
          </Button>
          <Button onClick={openCreate} data-testid="customers-new-button">
            <Plus size={16} /> {t("common.add")}
          </Button>
        </div>
      </div>

      {importResult && (
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent>
            <div className="text-sm">
              <strong>{t("customers.importResult")}:</strong> Nhập <strong>{importResult.inserted}</strong> · Bỏ qua: <strong>{importResult.skipped}</strong>
              {importResult.errors?.length > 0 && (
                <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
                  {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
            <button onClick={() => setImportResult(null)} className="text-xs text-bamboo hover:underline mt-2">Đóng</button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <div className="sm:col-span-2">
              <Label>{t("common.search")}</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tên / SĐT / Mã" className="pl-8" data-testid="customers-search" />
              </div>
            </div>
            <div>
              <Label>{t("customers.group")}</Label>
              <Select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="customers-group-filter">
                <option value="">{t("common.all")}</option>
                <option value="vip">{t("customers.vip")}</option>
                <option value="regular">{t("customers.regular")}</option>
                <option value="new">{t("customers.new")}</option>
              </Select>
            </div>
            <div>
              <Label>{t("common.district")}</Label>
              <Select value={district} onChange={(e) => setDistrict(e.target.value)} data-testid="customers-district-filter">
                <option value="">{t("common.all")}</option>
                {districts.map((d) => <option key={d} value={d}>{d}</option>)}
              </Select>
            </div>
            <div>
              <Label>{t("common.sort")}</Label>
              <div className="flex gap-1">
                <Select value={sort} onChange={(e) => setSort(e.target.value)} className="text-xs flex-1" data-testid="customers-sort-field">
                  <option value="created_at">Mới tạo</option>
                  <option value="name">Tên</option>
                  <option value="city">Tỉnh/Thành</option>
                  <option value="total_spent">Tổng chi</option>
                  <option value="total_orders">Số đơn</option>
                </Select>
                <Button type="button" size="sm" variant="outline" onClick={() => setDirection(direction === "asc" ? "desc" : "asc")} data-testid="customers-sort-direction" title={direction === "asc" ? "Tăng" : "Giảm"}>
                  {direction === "asc" ? "↑" : "↓"}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("code")}>Mã{SortIcon("code")}</th>
                <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("name")}>{t("common.name")}{SortIcon("name")}</th>
                <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("phone")}>{t("common.phone")}{SortIcon("phone")}</th>
                <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("district")}>Khu vực{SortIcon("district")}</th>
                <th className="text-center py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("group")}>{t("customers.group")}{SortIcon("group")}</th>
                <th className="text-right py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("total_spent")}>Tổng chi{SortIcon("total_spent")}</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody data-testid="customers-table-body">
              {items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-ink-muted">{t("common.empty")}</td></tr>
              ) : sortedItems.map((c) => (
                <tr key={c.customer_id} className="border-t border-border hover:bg-cream/30" data-testid={`customer-row-${c.customer_id}`}>
                  <td className="py-3 px-4 font-mono text-xs">{c.code}</td>
                  <td className="py-3 px-4">
                    <div className="font-medium">{c.name}</div>
                    {c.nickname && <div className="text-xs text-ink-muted">({c.nickname})</div>}
                  </td>
                  <td className="py-3 px-4 text-xs">{c.phone}</td>
                  <td className="py-3 px-4 text-xs">
                    {c.district || c.city ? `${c.district || ""}${c.district && c.city ? ", " : ""}${c.city || ""}` : "—"}
                  </td>
                  <td className="py-3 px-4 text-center"><Badge variant={c.group}>{t(`customers.${c.group}`)}</Badge></td>
                  <td className="py-3 px-4 text-right font-mono text-xs">
                    {formatVND(c.total_spent || 0)}
                    {(c.total_debt || 0) > 0 && (
                      <div className="text-[10px] text-red-500 font-semibold">Nợ: {formatVND(c.total_debt)}</div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-1">
                      {/* NÚT CHI TIẾT */}
                      <Button variant="ghost" size="icon" onClick={() => openDetail(c)} title="Chi tiết">
                        <Eye size={14} className="text-bamboo" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`customer-edit-${c.customer_id}`}>
                        <Edit size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`customer-delete-${c.customer_id}`}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* --- POPUP MODAL XEM CHI TIẾT KHÁCH HÀNG --- */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} title="Chi tiết khách hàng" size="lg">
        <div className="space-y-4">
          {/* THANH TAB ĐIỀU HƯỚNG */}
          <div className="flex border-b border-border">
            <button
              type="button"
              className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === "info" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"}`}
              onClick={() => setActiveTab("info")}
            >
              Thông tin khách hàng
            </button>
            <button
              type="button"
              className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === "history" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"}`}
              onClick={() => setActiveTab("history")}
            >
              Lịch sử mua hàng
            </button>
            <button
              type="button"
              className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === "debt" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"}`}
              onClick={() => setActiveTab("debt")}
            >
              Sổ công nợ
            </button>
            {canCustomPrices && (
              <button
                type="button"
                className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === "prices" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"}`}
                onClick={() => setActiveTab("prices")}
              >
                💰 Bảng giá riêng
              </button>
            )}
          </div>

          {/* BỘ LỌC NGÀY (CHỈ HIỂN THỊ KHI Ở TAB LỊCH SỬ HOẶC CÔNG NỢ) */}
          {(activeTab === "history" || activeTab === "debt") && (
            <div className="flex flex-col gap-3 bg-cream/20 p-3 rounded border border-border">
              {/* CÁC NÚT BỘ LỌC NHANH */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  type="button"
                  variant={filterType === "today" ? "default" : "outline"} 
                  size="sm" onClick={() => applyQuickFilter("today")} className="text-xs h-8">
                  Hôm nay
                </Button>
                <Button 
                  type="button"
                  variant={filterType === "week" ? "default" : "outline"} 
                  size="sm" onClick={() => applyQuickFilter("week")} className="text-xs h-8">
                  Tuần này
                </Button>
                <Button 
                  type="button"
                  variant={filterType === "month" ? "default" : "outline"} 
                  size="sm" onClick={() => applyQuickFilter("month")} className="text-xs h-8">
                  Tháng này
                </Button>
                <Button 
                  type="button"
                  variant={filterType === "year" ? "default" : "outline"} 
                  size="sm" onClick={() => applyQuickFilter("year")} className="text-xs h-8">
                  Năm nay
                </Button>
                <Button 
                  type="button"
                  variant={filterType === "all" ? "default" : "outline"} 
                  size="sm" onClick={() => applyQuickFilter("all")} className="text-xs h-8">
                  Tất cả / Tuỳ chọn
                </Button>
              </div>

              {/* INPUT TÙY CHỌN TỪ NGÀY - ĐẾN NGÀY */}
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-full sm:w-auto">
                  <Label className="text-xs">Từ ngày</Label>
                  <Input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => { setStartDate(e.target.value); setFilterType("custom"); }} 
                    className="h-9 text-xs" 
                  />
                </div>
                <div className="w-full sm:w-auto">
                  <Label className="text-xs">Đến ngày</Label>
                  <Input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => { setEndDate(e.target.value); setFilterType("custom"); }} 
                    className="h-9 text-xs" 
                  />
                </div>
                {/* NÚT XÓA LỌC HIỂN THỊ KHI ĐANG CÓ LỌC */}
                {(startDate || endDate) && (
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm" 
                    onClick={() => applyQuickFilter("all")} 
                    className="h-9 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    Xóa lọc
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => activeTab === "history" ? handlePrintHistory() : handlePrintDebt()}
                  className="h-9 text-xs ml-auto"
                >
                  <Printer size={13} className="mr-1" /> In danh sách
                </Button>
              </div>
            </div>
          )}

          {/* NỘI DUNG TỪNG TAB */}
          <div className="min-h-[250px]">
            {/* TAB 1: THÔNG TIN CHI TIẾT */}
            {activeTab === "info" && selectedCustomer && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm p-2">
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Mã khách hàng:</span>
                  <span className="font-mono">{selectedCustomer.code || "—"}</span>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Tên khách hàng:</span>
                  <span className="font-semibold">{selectedCustomer.name}</span>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Tên phụ / Biệt danh:</span>
                  <span>{selectedCustomer.nickname || "—"}</span>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Số điện thoại:</span>
                  <span>{selectedCustomer.phone || "—"}</span>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Email:</span>
                  <span>{selectedCustomer.email || "—"}</span>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Mã số thuế / CCCD:</span>
                  <span>{selectedCustomer.tax_id || "—"}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-ink-muted font-medium block text-xs">Địa chỉ:</span>
                  <span>
                    {selectedCustomer.address 
                      ? `${selectedCustomer.address}${selectedCustomer.district ? `, ${selectedCustomer.district}` : ""}${selectedCustomer.city ? `, ${selectedCustomer.city}` : ""}`
                      : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Nhóm khách hàng:</span>
                  <Badge variant={selectedCustomer.group}>{t(`customers.${selectedCustomer.group}`)}</Badge>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Loại khách hàng:</span>
                  <span>{t(`customers.${selectedCustomer.type || "retail"}`)}</span>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Phân loại tùy chỉnh:</span>
                  <span>{selectedCustomer.classification || "—"}</span>
                </div>
                <div>
                  <span className="text-ink-muted font-medium block text-xs">Tổng chi tiêu:</span>
                  <span className="font-mono font-bold text-bamboo">{formatVND(selectedCustomer.total_spent || 0)}</span>
                </div>
                {(selectedCustomer.total_debt || 0) > 0 && (
                  <div className="sm:col-span-2 bg-red-50 border border-red-200 rounded-lg p-3">
                    <span className="text-red-700 font-semibold text-xs block mb-1">⚠ Công nợ hiện tại</span>
                    <span className="font-mono text-red-600 text-lg font-bold">{formatVND(selectedCustomer.total_debt)}</span>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <span className="text-ink-muted font-medium block text-xs">Ghi chú:</span>
                  <p className="bg-cream/20 p-2 rounded border border-dashed mt-1 whitespace-pre-wrap text-xs text-ink-secondary">
                    {selectedCustomer.notes || "Không có ghi chú"}
                  </p>
                </div>
              </div>
            )}

            {/* TAB 2: LỊCH SỬ MUA HÀNG */}
            {activeTab === "history" && (
              <div className="overflow-x-auto max-h-[350px] border border-border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-cream/40 text-xs uppercase text-ink-muted sticky top-0">
                    <tr>
                      <th className="text-left py-2.5 px-3">Mã đơn</th>
                      <th className="text-left py-2.5 px-3">Ngày mua</th>
                      <th className="text-left py-2.5 px-3">Hàng hóa</th>
                      <th className="text-right py-2.5 px-3">Tổng tiền</th>
                      <th className="text-center py-2.5 px-3">Trạng thái</th>
                      <th className="py-2.5 px-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-ink-muted">Không có lịch sử mua hàng trong thời gian này.</td></tr>
                    ) : filteredHistory.map((h, idx) => (
                      <tr
                        key={h.order_id || idx}
                        className="border-t border-border hover:bg-bamboo/5 cursor-pointer transition-colors group"
                        onClick={() => openOrderDetail(h.order_id)}
                        title="Bấm để xem chi tiết đơn hàng"
                      >
                        <td className="py-2.5 px-3 font-mono text-xs font-semibold text-bamboo group-hover:underline">{h.order_code || "—"}</td>
                        <td className="py-2.5 px-3 text-xs">{h.created_at ? h.created_at.slice(0, 10) : "—"}</td>
                        <td className="py-2.5 px-3 text-xs text-ink-muted max-w-[160px]">
                          <div className="flex flex-wrap gap-1">
                            {(h.items || []).slice(0, 2).map((it, i) => (
                              <span key={i} className="bg-cream px-1.5 py-0.5 rounded text-[10px]">{it.name} ×{it.quantity}</span>
                            ))}
                            {(h.items || []).length > 2 && <span className="text-[10px] text-ink-muted">+{h.items.length - 2}</span>}
                            {(!h.items || h.items.length === 0) && "—"}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-xs text-bamboo font-semibold">{formatVND(h.total || 0)}</td>
                        <td className="py-2.5 px-3 text-center text-xs">
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {t(`orders.status.${h.status}`) || h.status}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-bamboo/60 group-hover:text-bamboo transition-colors">
                            <ChevronRight size={13} />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {filteredHistory.length > 0 && (
                    <tfoot className="border-t-2 border-border bg-cream/40 sticky bottom-0">
                      <tr>
                        <td colSpan={3} className="py-2 px-3 text-xs text-ink-muted font-semibold">{filteredHistory.length} đơn</td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-bamboo text-xs">
                          {formatVND(filteredHistory.reduce((s, h) => s + (h.total || 0), 0))}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {/* TAB 3: SỔ CÔNG NỢ */}
            {activeTab === "debt" && (
              <div className="overflow-x-auto max-h-[350px] border border-border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-cream/40 text-xs uppercase text-ink-muted sticky top-0">
                    <tr>
                      <th className="text-left py-2.5 px-3">Mã phiếu</th>
                      <th className="text-left py-2.5 px-3">Ngày ghi nhận</th>
                      <th className="text-right py-2.5 px-3">Số tiền nợ</th>
                      <th className="text-right py-2.5 px-3">Đã trả</th>
                      <th className="text-left py-2.5 px-3">Ghi chú</th>
                      <th className="py-2.5 px-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDebts.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-ink-muted">Không ghi nhận lịch sử công nợ trong thời gian này.</td></tr>
                    ) : filteredDebts.map((d, idx) => (
                      <tr
                        key={d.order_id || idx}
                        className="border-t border-border hover:bg-bamboo/5 cursor-pointer transition-colors group"
                        onClick={() => openOrderDetail(d.order_id)}
                        title="Bấm để xem chi tiết đơn hàng"
                      >
                        <td className="py-2.5 px-3 font-mono text-xs font-semibold text-bamboo group-hover:underline">{d.order_code || "—"}</td>
                        <td className="py-2.5 px-3 text-xs">{d.created_at ? d.created_at.slice(0, 10) : "—"}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-xs text-red-600 font-semibold">{formatVND(d.remaining_amount || 0)}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-xs text-emerald-600">{formatVND(d.paid_amount || 0)}</td>
                        <td className="py-2.5 px-3 text-xs text-ink-muted">{d.note || "—"}</td>
                        <td className="py-2.5 px-3 text-center">
                          <span className="inline-flex items-center text-bamboo/60 group-hover:text-bamboo transition-colors">
                            <ChevronRight size={13} />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ─── TAB BẢNG GIÁ RIÊNG ─────────────────────────────────── */}
            {activeTab === "prices" && canCustomPrices && (
              <div className="space-y-3">
                <p className="text-xs text-ink-muted">
                  Giá riêng sẽ được tự động áp dụng khi tạo đơn cho khách này.
                  Các sản phẩm không có giá riêng sẽ dùng giá gốc.
                </p>
                <div className="overflow-x-auto border border-border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                      <tr>
                        <th className="text-left py-2.5 px-3">Sản phẩm</th>
                        <th className="text-right py-2.5 px-3">Giá gốc</th>
                        <th className="text-right py-2.5 px-3 w-36">Giá riêng</th>
                        <th className="py-2.5 px-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {allProducts.map((prod) => {
                        const entry = customerPrices.find(p => p.product_id === prod.product_id);
                        return (
                          <tr key={prod.product_id} className="border-t border-border hover:bg-bamboo/5">
                            <td className="py-2 px-3">
                              <div className="font-medium text-sm">{prod.name}</div>
                              <div className="text-xs text-ink-muted">{prod.category || ""}</div>
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-xs text-ink-muted">
                              {formatVND(prod.price)}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <input
                                type="number"
                                min={0}
                                placeholder="Giá gốc"
                                value={entry?.price ?? ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setCustomerPrices(prev => {
                                    const filtered = prev.filter(p => p.product_id !== prod.product_id);
                                    if (val !== "") return [...filtered, { product_id: prod.product_id, price: Number(val) }];
                                    return filtered;
                                  });
                                }}
                                className="w-28 text-right border border-border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-bamboo"
                              />
                            </td>
                            <td className="py-2 px-3 text-center">
                              {entry && (
                                <button
                                  type="button"
                                  title="Xóa giá riêng"
                                  onClick={() => setCustomerPrices(prev => prev.filter(p => p.product_id !== prod.product_id))}
                                  className="text-red-400 hover:text-red-600 text-xs"
                                >✕</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    disabled={savingPrices}
                    onClick={async () => {
                      setSavingPrices(true);
                      try {
                        await api.put(`/customers/${selectedCustomer.customer_id}/prices`, { prices: customerPrices });
                        toast({ title: "✅ Đã lưu bảng giá riêng!", variant: "success" });
                      } catch (_) {
                        toast({ title: "Lỗi lưu bảng giá", variant: "error" });
                      }
                      setSavingPrices(false);
                    }}
                  >
                    {savingPrices ? "Đang lưu..." : "💾 Lưu bảng giá"}
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end pt-2 border-t border-border">
            <Button variant="outline" type="button" onClick={() => setDetailOpen(false)}>Đóng</Button>
          </div>
        </div>
      </Modal>

      {/* --- MODAL THÊM / SỬA KHÁCH HÀNG --- */}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? t("common.edit") : t("common.add")} size="lg" testId="customer-modal">
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("customers.code")}</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Tự sinh" data-testid="customer-form-code" />
            </div>
            <div>
              <Label>{t("common.name")} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="customer-form-name" />
            </div>
            <div>
              <Label>{t("customers.nickname")}</Label>
              <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} placeholder="VD: Chị Lan, Anh Hùng..." data-testid="customer-form-nickname" />
            </div>
            <div>
              <Label>{t("customers.taxId")}</Label>
              <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="MST hoặc CCCD" />
            </div>
            <div>
              <Label>{t("common.phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-form-phone" />
            </div>
            <div>
              <Label>{t("common.email")}</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>{t("common.address")}</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Địa chỉ chi tiết" data-testid="customer-form-address" />
            </div>
            <div>
              <Label>{t("common.district")}</Label>
              <Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} placeholder="VD: Phường 1" data-testid="customer-form-district" />
            </div>
            <div>
              <Label>{t("common.city")}</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="VD: TP.HCM" />
            </div>
            <div>
              <Label>{t("customers.group")}</Label>
              <Select value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })}>
                <option value="new">{t("customers.new")}</option>
                <option value="regular">{t("customers.regular")}</option>
                <option value="vip">{t("customers.vip")}</option>
              </Select>
            </div>
            <div>
              <Label>{t("customers.type")}</Label>
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="customer-form-type">
                <option value="retail">{t("customers.retail")}</option>
                <option value="wholesale">{t("customers.wholesale")}</option>
              </Select>
            </div>
            <div>
              <Label>{t("customers.classification")}</Label>
              <Input value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })} placeholder="Tự tạo nhóm tùy chỉnh" data-testid="customer-form-classification" />
            </div>
            <div>
              <Label>{t("customers.assignedUser")}</Label>
              <Select value={form.assigned_user_id} onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })} data-testid="customer-form-assigned-user">
                <option value="">— Không —</option>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </Select>
            </div>
            <div>
              <Label>{t("customers.maxDebtDays")}</Label>
              <Input type="number" min={0} value={form.max_debt_days} onChange={(e) => setForm({ ...form, max_debt_days: Number(e.target.value) })} data-testid="customer-form-max-debt-days" />
            </div>
            <div>
              <Label>{t("customers.maxDebtAmount")} (VND)</Label>
              <Input type="number" min={0} value={form.max_debt_amount} onChange={(e) => setForm({ ...form, max_debt_amount: Number(e.target.value) })} data-testid="customer-form-max-debt-amount" />
            </div>
          </div>

          {/* Công nợ đầu kỳ */}
          <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-3 space-y-1.5">
            <Label className="flex items-center gap-1.5 text-amber-800 font-semibold">
              Công nợ đầu kỳ (VNĐ)
              <span className="text-[10px] font-normal bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full">Tuỳ chọn</span>
            </Label>
            <Input
              type="number" min={0}
              value={form.opening_balance}
              onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })}
              placeholder="0 — nếu khách không có nợ cũ"
              data-testid="customer-form-opening-balance"
            />
            <p className="text-xs text-amber-700">
              {editing
                ? "Chỉnh sửa số tiền nợ đầu kỳ từ trước khi chuyển sang hệ thống này."
                : <>Nhập số tiền khách đang <strong>nợ từ trước</strong> khi chuyển sang hệ thống này. Hệ thống sẽ tạo phiếu nợ đầu kỳ tự động để theo dõi.</>
              }
            </p>
          </div>

          <div>
            <Label>{t("common.notes")}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" data-testid="customer-form-submit">{t("common.save")}</Button>
          </div>
        </form>
      </Modal>

      {/* --- PORTAL: CHI TIẾT ĐƠN HÀNG --- */}
      {(orderDetail || orderDetailLoading) && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOrderDetail(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[85vh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOrderDetail(null)}
                  className="text-ink-muted hover:text-ink mr-1 p-1 rounded-lg hover:bg-cream transition-colors"
                >
                  <ArrowLeft size={16} />
                </button>
                <div>
                  <h3 className="font-heading font-bold text-base flex items-center gap-2">
                    Đơn hàng
                    {orderDetail && <span className="font-mono text-bamboo">{orderDetail.order_code}</span>}
                  </h3>
                  {orderDetail && (
                    <p className="text-xs text-ink-muted mt-0.5">
                      {orderDetail.created_at ? orderDetail.created_at.slice(0, 10) : ""}
                      {orderDetail.assigned_shipper_name && ` · Shipper: ${orderDetail.assigned_shipper_name}`}
                    </p>
                  )}
                </div>
              </div>
              <button onClick={() => setOrderDetail(null)} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-cream">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
              {orderDetailLoading ? (
                <div className="py-16 text-center text-sm text-ink-muted animate-pulse">Đang tải đơn hàng...</div>
              ) : orderDetail ? (
                <>
                  {/* Trạng thái + HTTT */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={orderDetail.status === "delivered" ? "delivered" : orderDetail.status === "delivering" ? "processing" : orderDetail.status === "cancelled" ? "cancelled" : "regular"}>
                      {t(`orders.status.${orderDetail.status}`) || orderDetail.status}
                    </Badge>
                    <Badge variant={orderDetail.payment_method === "debt" ? "lowstock" : "regular"}>
                      {{ cash: "Tiền mặt", transfer: "Chuyển khoản", ewallet: "Ví điện tử", card: "Thẻ", debt: "Công nợ" }[orderDetail.payment_method] || orderDetail.payment_method}
                    </Badge>
                    {orderDetail.delivery_shift && (
                      <Badge variant="regular">
                        {orderDetail.delivery_shift === "morning" ? "☀️ Ca Sáng" : "🌤️ Ca Chiều"}
                      </Badge>
                    )}
                  </div>

                  {/* Địa chỉ giao */}
                  {(orderDetail.customer_district || orderDetail.customer_address) && (
                    <div className="flex items-start gap-2 text-sm bg-cream/30 rounded-lg px-3 py-2 border border-border/60">
                      <MapPin size={14} className="text-bamboo mt-0.5 shrink-0" />
                      <span className="text-ink-secondary">
                        {[orderDetail.customer_district, orderDetail.customer_address].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}

                  {/* Danh sách hàng hóa */}
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 flex items-center gap-1">
                      <Package size={11} /> Hàng hóa
                    </div>
                    <div className="border border-border rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-cream/50 text-[11px] uppercase text-ink-muted">
                          <tr>
                            <th className="text-left py-2 px-3">Sản phẩm</th>
                            <th className="text-center py-2 px-3">Số lượng</th>
                            <th className="text-right py-2 px-3">Đơn giá</th>
                            <th className="text-right py-2 px-3">Thành tiền</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(orderDetail.items || []).map((it, i) => (
                            <tr key={i} className="border-t border-border/50 hover:bg-cream/20">
                              <td className="py-2.5 px-3 font-medium">{it.name}</td>
                              <td className="py-2.5 px-3 text-center font-mono">{it.quantity} <span className="text-xs text-ink-muted">{it.unit || ""}</span></td>
                              <td className="py-2.5 px-3 text-right font-mono text-ink-muted text-xs">{formatVND(it.unit_price || 0)}</td>
                              <td className="py-2.5 px-3 text-right font-mono font-semibold text-bamboo">{formatVND((it.unit_price || 0) * (it.quantity || 0))}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t-2 border-border bg-cream/30">
                          <tr>
                            <td colSpan={3} className="py-2.5 px-3 text-right font-semibold text-sm">TỔNG CỘNG</td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-bamboo text-base">{formatVND(orderDetail.total || 0)}</td>
                          </tr>
                          {(orderDetail.remaining_amount || 0) > 0 && (
                            <tr>
                              <td colSpan={3} className="py-1.5 px-3 text-right text-xs text-red-600 font-semibold">Còn nợ</td>
                              <td className="py-1.5 px-3 text-right font-mono font-bold text-red-600">{formatVND(orderDetail.remaining_amount)}</td>
                            </tr>
                          )}
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Ghi chú */}
                  {orderDetail.note && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                      <span className="font-semibold text-xs uppercase tracking-wider block mb-1">Ghi chú</span>
                      {orderDetail.note}
                    </div>
                  )}

                  {/* Timeline */}
                  {(orderDetail.timeline || []).length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 flex items-center gap-1">
                        <Clock size={11} /> Lịch sử trạng thái
                      </div>
                      <div className="space-y-2">
                        {[...(orderDetail.timeline || [])].reverse().map((ev, i) => (
                          <div key={i} className="flex gap-3 text-xs">
                            <div className="w-1.5 h-1.5 rounded-full bg-bamboo mt-1.5 shrink-0" />
                            <div>
                              <span className="font-semibold text-ink">{ev.status}</span>
                              {ev.note && <span className="text-ink-muted ml-1">— {ev.note}</span>}
                              <div className="text-ink-muted mt-0.5">
                                {ev.at ? (typeof ev.at === "string" ? ev.at.slice(0, 16).replace("T", " ") : "") : ""}
                                {ev.by && ` · ${ev.by}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>,
        document.body
      )}

      <Modal open={dupOpen} onClose={() => setDupOpen(false)} title="Khách hàng bị trùng mã KH" size="lg">
        {!dupCodes || dupCodes.length === 0 ? (
          <p className="text-sm text-ink-secondary py-4">Không có mã KH bị trùng.</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-ink-secondary">
              Phát hiện <strong>{dupCodes.length}</strong> mã KH đang được dùng cho nhiều khách hàng. Sửa mã của các khách hàng dưới đây để mỗi mã chỉ thuộc về 1 khách hàng (nhấn vào tên để sửa).
            </p>
            {dupCodes.map((d) => (
              <div key={d.code} className="border border-amber-200 bg-amber-50/50 rounded-lg p-3">
                <div className="font-mono font-semibold text-sm text-amber-800 mb-2">Mã "{d.code}" — dùng cho {d.count} khách hàng</div>
                <div className="space-y-1">
                  {d.customers.map((c) => {
                    const full = items.find((i) => i.customer_id === c.customer_id);
                    return (
                      <button
                        key={c.customer_id}
                        type="button"
                        onClick={() => { if (full) { setDupOpen(false); openEdit(full); } }}
                        className="w-full text-left text-sm flex items-center justify-between gap-2 bg-white rounded-md px-3 py-2 border border-amber-100 hover:border-bamboo hover:bg-bamboo/5 transition"
                      >
                        <span>
                          <span className="font-medium">{c.name}</span>
                          {c.phone && <span className="text-ink-muted"> · {c.phone}</span>}
                        </span>
                        <span className="text-xs text-ink-muted">{c.total_orders || 0} đơn · {formatDate(c.created_at)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}